// src/components/onboarding/OnboardingTicketsModal.jsx
//
// The ticket review modal for Stage C (backend "B": block / Intune / Copper /
// Bronze tickets) and Stage D (backend "C": the onboarding ticket and its
// checklist library) — the quote-push treatment: title and description seeded
// from the plan and editable, include/skip, Issue Type / Sub-Issue Type (the
// shared fields over ONE picklist fetch, passed in by the panel). Nothing
// reaches Autotask until Craig presses Create. `stages` (BACKEND letters)
// selects which tickets this opening reviews; the heading comes from the
// postWonSteps display map, never from the backend letter.
//
// ⛔ THE TRAP THIS MUST NOT WALK INTO: an edited title never becomes the
// ticket's identity. Overrides are keyed by `matchKey` (derived from the source
// block, server-side) and the server records `blockTitle` = what was sent; so
// editing a title here cannot make the next run fail to recognise the ticket
// it created and make a second one. ALREADY-CREATED tickets (record.ticketIds
// matched the three ways runStageB matches them, or record.onboardingTicketId)
// render as "already created — T…" with no fields and are never offered again.
//
// Stage D's two halves are reported SEPARATELY, as the service records them:
// the onboarding ticket is verifiable (its id came back); the checklist-library
// attach is NOT (TicketChecklistLibraries is canQuery=false) and is shown as
// "reported OK (cannot be read back)", never as verified.
import React, { useEffect, useState } from "react";
import TicketClassificationFields from "../quotes/TicketClassificationFields";
import { ticketsForReview, initialTicketEdits, buildTicketOverrides, ticketOutcome } from "./onboardingReview";
import { stepForBackendCode, stepTitle } from "./postWonSteps";

const KIND_NOTE = {
  block: "foundational block",
  intune: "Intune setup fee charged",
  "intune-enrolment": "per-device enrolment charged",
  copper: "Copper Defender devices",
  "bronze-devices": "Bronze grouping devices",
};
const contractNote = (t) => (!t.contract
  ? "no contract (needs the labour category)"
  : `contract $${Number(t.contract.price).toFixed(2)} / ${t.contract.hours}h${t.contract.price > 0 ? "" : " (no milestone)"}`);

/* A reviewed item belongs to backend stage "B" (Stage B tickets) or "C" (the
   onboarding ticket). */
const codeOf = (item) => (item.stage === "C" ? "C" : "B");

export default function OnboardingTicketsModal({ plan, record, stages = ["B", "C"], picklists, picklistsError, busy, result, onCancel, onConfirm }) {
  const all = ticketsForReview(plan, record);
  const items = all.filter((it) => stages.includes(codeOf(it)));
  const [edits, setEdits] = useState(() => initialTicketEdits(items));
  /* ⚠ Keyed on a STRING, not the `stages` array. Every caller passes a fresh
     array literal, so depending on the array re-fires this effect on every
     render, setEdits re-renders, and the loop is synchronous — it blocked the
     event loop so hard that Jest could not even time out. */
  const stagesKey = stages.join(",");
  useEffect(() => {
    setEdits(initialTicketEdits(ticketsForReview(plan, record).filter((it) => stagesKey.split(",").includes(codeOf(it)))));
  }, [plan, record, stagesKey]);

  if (!plan) return null;
  const pending = items.filter((it) => !it.existing);
  const toCreate = pending.filter((it) => edits[it.key]?.include !== false);
  const setEdit = (key, patch) => setEdits((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  const canConfirm = !busy && !result && toCreate.length > 0;
  const confirm = () => onConfirm({ overrides: buildTicketOverrides(items, edits) });
  const heading = stages.map((c) => stepForBackendCode(c)).filter(Boolean).map(stepTitle).join(" · ") || "Tickets";

  const card = { border: "1px solid var(--border,#e2e8f0)", borderRadius: 8, padding: "10px 12px", marginBottom: 10 };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(11,34,64,0.45)", zIndex: 500,
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
         onClick={busy ? undefined : onCancel}>
      <div style={{ background: "var(--s1,#fff)", borderRadius: 10, padding: 22, width: 760, maxWidth: "100%",
                    maxHeight: "92vh", overflowY: "auto", border: "1px solid var(--border,#e2e8f0)" }}
           onClick={(e) => e.stopPropagation()} data-testid="onboarding-tickets-modal">
        <div style={{ fontFamily: "var(--ff-head)", fontWeight: 700, fontSize: 15, marginBottom: 4 }} data-testid="tickets-modal-heading">
          {heading} · {plan.clientName}
        </div>
        <div style={{ fontSize: 11, color: "var(--text3,#777)", marginBottom: 12, lineHeight: 1.6 }}>
          Projects queue · Autotask company {plan.companyID ? `#${plan.companyID}` : "NOT SET"} · Source: Web Portal.
          Each ticket below is created with its own fixed-price contract where one applies. Untick a ticket to leave it for a later run —
          <strong> skip means not now, not never</strong>. Nothing is sent until you press Create.
        </div>

        {items.map((it) => {
          const e = edits[it.key];
          const t = it.ticket;
          const outcome = result ? ticketOutcome(it, result, edits) : null;
          const step = stepForBackendCode(codeOf(it));
          const header = (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 9.5, fontWeight: 700, padding: "2px 7px", borderRadius: 20, background: it.stage === "C" ? "#e0e7ff" : "#eef2f7", color: it.stage === "C" ? "#3730a3" : "#475569", textTransform: "uppercase", letterSpacing: ".06em" }}>
                Stage {step ? step.letter : it.stage}
              </span>
              <span style={{ fontSize: 10, color: "var(--text3,#777)" }}>{it.stage === "C" ? "onboarding ticket + checklist library" : (KIND_NOTE[t.kind] || t.kind)} · {contractNote(t)}</span>
            </div>
          );

          if (it.existing && !result) {
            return (
              <div key={it.key} style={{ ...card, background: "var(--s2,#f8fafc)" }} data-testid={`ticket-${it.key}`}>
                {header}
                <div style={{ fontSize: 12, fontWeight: 600 }}>{it.existing.blockTitle || t.title}</div>
                <div data-testid={`ticket-${it.key}-done`} style={{ fontSize: 11, color: "#15803d", marginTop: 2 }}>
                  Already created — {it.existing.ticketNumber || `ticket ${it.existing.ticketId}`}. Not offered again.
                </div>
              </div>
            );
          }

          if (result) {
            return (
              <div key={it.key} style={card} data-testid={`ticket-${it.key}`}>
                {header}
                <div style={{ fontSize: 12, fontWeight: 600 }}>{(e && e.title) || t.title}</div>
                {it.existing && !outcome?.ticketNumber && <div style={{ fontSize: 11, color: "#15803d" }}>Already created — {it.existing.ticketNumber || `ticket ${it.existing.ticketId}`}.</div>}
                {outcome?.state === "created" && (
                  <div data-testid={`ticket-${it.key}-outcome`} style={{ fontSize: 11, color: "#15803d", marginTop: 2, lineHeight: 1.6 }}>
                    Ticket created — <strong>{outcome.ticketNumber || `ticket ${outcome.ticketId}`}</strong> (verified: the id came back from Autotask).
                    {it.stage === "C" && outcome.attach && (
                      <div style={{ color: outcome.attach.state === "reported-ok" ? "#92400e" : "#991b1b" }}>
                        {outcome.attach.state === "reported-ok"
                          ? <>Checklist library attach <strong>reported OK</strong>{outcome.attach.status ? ` (HTTP ${outcome.attach.status})` : ""} — <strong>cannot be read back</strong> through the API; check the ticket's Checklist tab.</>
                          : <>Checklist library attach <strong>FAILED</strong>{outcome.attach.error ? `: ${outcome.attach.error}` : ""} — a re-run re-attempts it against this same ticket.</>}
                      </div>
                    )}
                  </div>
                )}
                {outcome?.state === "skipped" && <div data-testid={`ticket-${it.key}-outcome`} style={{ fontSize: 11, color: "var(--text3,#777)", marginTop: 2 }}>Skipped this run — nothing recorded; a later run will offer it again.</div>}
                {outcome?.state === "failed" && <div data-testid={`ticket-${it.key}-outcome`} style={{ fontSize: 11, color: "#991b1b", marginTop: 2 }}>Not created{outcome.error ? ` — ${outcome.error}` : ""}. Re-run to try again.</div>}
              </div>
            );
          }

          return (
            <div key={it.key} style={{ ...card, opacity: e?.include === false ? 0.6 : 1 }} data-testid={`ticket-${it.key}`}>
              {header}
              <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 11, marginBottom: 6, cursor: "pointer" }}>
                <input type="checkbox" aria-label={`Create ${t.title}`} checked={e?.include !== false} disabled={!!busy}
                       onChange={(ev) => setEdit(it.key, { include: ev.target.checked })} />
                <strong>Create this ticket</strong>{e?.include === false ? " — skipped this run" : ""}
              </label>
              <label htmlFor={`ticket-${it.key}-title`} style={{ fontSize: 11, fontWeight: 600, color: "var(--text2,#555)" }}>Ticket title</label>
              <input id={`ticket-${it.key}-title`} className="inp" aria-label={`Title for ${t.title}`} value={e?.title ?? ""} disabled={!!busy || e?.include === false}
                     onChange={(ev) => setEdit(it.key, { title: ev.target.value })} style={{ fontSize: 12, marginBottom: 8 }} maxLength={255} />
              <label htmlFor={`ticket-${it.key}-description`} style={{ fontSize: 11, fontWeight: 600, color: "var(--text2,#555)" }}>Description</label>
              <textarea id={`ticket-${it.key}-description`} className="inp" aria-label={`Description for ${t.title}`} rows={4} value={e?.description ?? ""} disabled={!!busy || e?.include === false}
                        onChange={(ev) => setEdit(it.key, { description: ev.target.value })}
                        style={{ fontSize: 11.5, lineHeight: 1.5, resize: "vertical", fontFamily: "ui-monospace, Menlo, Consolas, monospace", marginBottom: 8 }} />
              <TicketClassificationFields picklists={picklists} error={picklistsError} disabled={!!busy || e?.include === false}
                                          idPrefix={`ticket-${it.key}`} unavailableTestId={`ticket-${it.key}-classification-unavailable`}
                                          issueType={e?.issueType ?? ""} subIssueType={e?.subIssueType ?? ""}
                                          onChange={(c) => setEdit(it.key, c)} />
              {it.stage === "C" && (
                <div style={{ fontSize: 10, color: "var(--text3,#777)", lineHeight: 1.5 }}>
                  After the ticket is created, the {plan.tier || "tier"} checklist library{plan.stageC?.checklistLibraryID ? ` (${plan.stageC.checklistLibraryID})` : ""} is attached.
                  The attach result is an <strong>outcome</strong>, not a verified state — it cannot be read back through the API.
                </div>
              )}
            </div>
          );
        })}

        {items.length === 0 && <div style={{ fontSize: 11, color: "var(--text3,#777)", marginBottom: 10 }}>This plan creates no tickets for this stage.</div>}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center", marginTop: 6 }}>
          <span style={{ fontSize: 10, color: "var(--text3,#777)", marginRight: "auto" }}>
            {result
              ? `${items.filter((it) => ticketOutcome(it, result, edits).state === "created").length} created · ${items.filter((it) => ticketOutcome(it, result, edits).state === "skipped").length} skipped · ${items.filter((it) => ticketOutcome(it, result, edits).state === "failed").length} failed`
              : `${toCreate.length} to create · ${pending.length - toCreate.length} skipped · ${items.length - pending.length} already created`}
          </span>
          {result ? (
            <button className="btn btn-strat" style={{ fontSize: 11 }} onClick={onCancel}>Done</button>
          ) : (
            <>
              <button className="btn" style={{ fontSize: 11 }} onClick={onCancel} disabled={!!busy}>Cancel</button>
              <button className="btn btn-strat" style={{ fontSize: 11 }} disabled={!canConfirm} onClick={confirm}>
                {busy ? "Creating…" : `Create ${toCreate.length} ticket${toCreate.length === 1 ? "" : "s"} in Autotask`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
